import React from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { ChatBubbleProps } from '../types';

/**
 * Individual chat message bubble component
 * Displays message content with proper styling for sender/receiver
 */
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  isOwnMessage,
  showAvatar = false,
  className,
}) => {
  const formatTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isOwnMessage ? 'justify-end' : 'justify-start',
        className
      )}
    >
      <div
        className={cn(
          'flex max-w-[80%] gap-2',
          isOwnMessage ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        {/* Avatar placeholder - for future enhancement */}
        {showAvatar && (
          <div
            className={cn(
              'w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium',
              isOwnMessage ? 'ml-2' : 'mr-2'
            )}
          >
            {isOwnMessage ? 'You' : 'A'}
          </div>
        )}

        {/* Message content */}
        <div
          className={cn(
            'flex flex-col',
            isOwnMessage ? 'items-end' : 'items-start'
          )}
        >
          {/* Message bubble */}
          <div
            className={cn(
              'px-4 py-2 rounded-2xl max-w-full break-words',
              isOwnMessage
                ? 'bg-primary text-primary-foreground rounded-br-md'
                : 'bg-muted text-foreground rounded-bl-md'
            )}
          >
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.content}
            </p>
          </div>

          {/* Timestamp */}
          <div
            className={cn(
              'mt-1 px-2',
              isOwnMessage ? 'text-right' : 'text-left'
            )}
          >
            <span className="text-xs text-muted-foreground">
              {formatTime(message.createdAt)}
              {message.readAt && isOwnMessage && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ✓
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
